import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import type {
  GetTeamMemberResourceACLRes,
  GetTeamResourceACLRes,
  QuotaItem,
} from '@/api/type-response'
import { message } from '@/components'
import type { Scope } from '@/pages/teams/permissionQuota/types'

import type { PermissionForm, QuotaLimits } from './types'

const Q = {
  MESH_NET: 1,
  MESH_ENDPOINT: 2,
  INSTANCE_QTY: 3,
  SNAP_QTY: 4,
  STORAGE_BYTES: 5,
} as const
const uniq = <T>(arr: T[]) => Array.from(new Set(arr))

function getQuota(items: QuotaItem[] | undefined, type: number, fallback: number) {
  return (items ?? []).find((q) => q.QuotaType === type)?.QuotaValue ?? fallback
}

function extractQuotaLimits(items: QuotaItem[] | undefined): QuotaLimits {
  const getMax = (type: number, fallback: number) =>
    (items ?? []).find((q) => q.QuotaType === type)?.QuotaValueMax ?? fallback

  return {
    meshNet: getMax(Q.MESH_NET, 10),
    instanceQty: getMax(Q.INSTANCE_QTY, 200),
    snapQty: getMax(Q.SNAP_QTY, 200),
    storageBytes: getMax(Q.STORAGE_BYTES, 2199023255552),
  }
}

function emptyForm(): PermissionForm {
  return {
    enableACL: false,
    regions: [],
    gpuMachineCap: false,
    cpuMachineCap: false,
    vgpuMachineCap: false,
    processorModels: [],
    officialImageCap: false,
    images: [],
    meshNetQuota: 0,
    meshEndpointQuota: 5,
    instanceQtyQuota: 0,
    snapQtyQuota: 0,
    storageCapBytesQuota: 0,
  }
}

function fromTeamRes(r: GetTeamResourceACLRes): PermissionForm {
  const opt = r.acl.options
  const qs = r.acl.quotas?.items ?? []

  return {
    enableACL: !!opt.enableACL,
    regions: opt.regionOptions?.allowedRegions ?? [],
    gpuMachineCap: !!opt.machineOptions?.gpuMachineCap,
    cpuMachineCap: !!opt.machineOptions?.cpuMachineCap,
    vgpuMachineCap: !!opt.machineOptions?.vgpuMachineCap,
    processorModels: opt.machineOptions?.allowedProcessorModels ?? [],
    officialImageCap: !!opt.imageOptions?.officialImageCap,
    images: opt.imageOptions?.allowedImages ?? [],
    meshNetQuota: getQuota(qs, Q.MESH_NET, 0),
    meshEndpointQuota: getQuota(qs, Q.MESH_ENDPOINT, 5) || 5, // default 5, if change, discuss with PM
    instanceQtyQuota: getQuota(qs, Q.INSTANCE_QTY, 0),
    snapQtyQuota: getQuota(qs, Q.SNAP_QTY, 0),
    storageCapBytesQuota: getQuota(qs, Q.STORAGE_BYTES, 0),
  }
}

function fromMemberRes(r: GetTeamMemberResourceACLRes): {
  enableACL: boolean
  form: PermissionForm
  presentTypes: Set<number>
} {
  const opt = r.acl.options
  const qs = r.acl.quotas?.items ?? []
  const presentTypes = new Set(qs.map((q) => q.QuotaType))

  const f: PermissionForm = {
    enableACL: !!opt.enableACL,
    regions: opt.regionOptions?.allowedRegions ?? [],
    gpuMachineCap: !!opt.machineOptions?.gpuMachineCap,
    cpuMachineCap: !!opt.machineOptions?.cpuMachineCap,
    vgpuMachineCap: !!opt.machineOptions?.vgpuMachineCap,
    processorModels: opt.machineOptions?.allowedProcessorModels ?? [],
    officialImageCap: !!opt.imageOptions?.officialImageCap,
    images: opt.imageOptions?.allowedImages ?? [],
    meshNetQuota: getQuota(qs, Q.MESH_NET, 0),
    meshEndpointQuota: getQuota(qs, Q.MESH_ENDPOINT, 5) || 5, // default 5, if change, discuss with PM
    instanceQtyQuota: getQuota(qs, Q.INSTANCE_QTY, 0),
    snapQtyQuota: getQuota(qs, Q.SNAP_QTY, 0),
    storageCapBytesQuota: getQuota(qs, Q.STORAGE_BYTES, 0),
  }
  return { enableACL: f.enableACL, form: f, presentTypes }
}

function mergeTeamAndMember(
  team: PermissionForm,
  member: PermissionForm,
  presentTypes: Set<number>,
): PermissionForm {
  const pick = (type: number, mv: number, tv: number) => (presentTypes.has(type) ? mv : tv)

  return {
    enableACL: true,
    regions: uniq([...(team.regions ?? []), ...(member.regions ?? [])]).sort((a, b) => a - b),
    gpuMachineCap: !!(team.gpuMachineCap || member.gpuMachineCap),
    cpuMachineCap: !!(team.cpuMachineCap || member.cpuMachineCap),
    vgpuMachineCap: !!(team.vgpuMachineCap || member.vgpuMachineCap),
    processorModels: uniq([...(team.processorModels ?? []), ...(member.processorModels ?? [])]),
    officialImageCap: !!(team.officialImageCap || member.officialImageCap),
    images: uniq([...(team.images ?? []), ...(member.images ?? [])]),

    meshNetQuota: pick(Q.MESH_NET, member.meshNetQuota, team.meshNetQuota),
    meshEndpointQuota: pick(Q.MESH_ENDPOINT, member.meshEndpointQuota, team.meshEndpointQuota),
    instanceQtyQuota: pick(Q.INSTANCE_QTY, member.instanceQtyQuota, team.instanceQtyQuota),
    snapQtyQuota: pick(Q.SNAP_QTY, member.snapQtyQuota, team.snapQtyQuota),
    storageCapBytesQuota: pick(
      Q.STORAGE_BYTES,
      member.storageCapBytesQuota,
      team.storageCapBytesQuota,
    ),
  }
}

export function useResourceACL(scope: Scope, teamResID: string, memberResID: string) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState<PermissionForm>(emptyForm())
  const [teamUsingSystemDefault, setTeamUsingSystemDefault] = useState(false)

  const [teamDefaults, setTeamDefaults] = useState<PermissionForm | undefined>(undefined)
  const [hasPersonalQuotas, setHasPersonalQuotas] = useState(false)
  const [memberSnapshot, setMemberSnapshot] = useState<PermissionForm | undefined>(undefined)
  const [memberPresentTypes, setMemberPresentTypes] = useState<Set<number>>(new Set())
  const [quotaLimits, setQuotaLimits] = useState<QuotaLimits | undefined>(undefined)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setLoading(true)

        if (scope === 'team') {
          const team = await API.getTeamResourceACL({ res_id: teamResID })
          if (!alive) return
          const teamF = fromTeamRes(team)
          setTeamUsingSystemDefault(!team.acl.options?.enableACL)
          setQuotaLimits(extractQuotaLimits(team.acl.quotas?.items))

          setForm({ ...teamF, enableACL: true })
          setTeamDefaults(undefined)
          setMemberSnapshot(undefined)
          setHasPersonalQuotas(false)
          setMemberPresentTypes(new Set())
          return
        }

        // member
        const [team, member] = await Promise.all([
          API.getTeamResourceACL({ res_id: teamResID }),
          API.getTeamMemberResourceACL({ res_id: memberResID }),
        ])
        if (!alive) return

        const teamF = fromTeamRes(team)
        setTeamDefaults(teamF)
        setQuotaLimits(extractQuotaLimits(team.acl.quotas?.items))

        const { enableACL, form: memberF, presentTypes } = fromMemberRes(member)
        setMemberSnapshot(memberF)
        setMemberPresentTypes(presentTypes)
        setHasPersonalQuotas(presentTypes.size > 0)

        const initial = enableACL ? memberF : { ...teamF, enableACL: false }

        setForm(initial)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [scope, teamResID, memberResID])

  const patch = async (next: PermissionForm): Promise<{ code: number }> => {
    let res: { code: number }
    if (scope === 'team') {
      res = await API.updateTeamResourceACL({
        res_id: teamResID,
        enable: true,
        regions: next.regions,
        gpu_machine_cap: next.gpuMachineCap,
        cpu_machine_cap: next.cpuMachineCap,
        vgpu_machine_cap: next.vgpuMachineCap,
        processor_models: next.processorModels,
        official_image_cap: next.officialImageCap,
        images: next.images,
        mesh_net_quota: next.meshNetQuota,
        mesh_endpoint_quota: next.meshEndpointQuota,
        instance_qty_quota: next.instanceQtyQuota,
        snap_qty_quota: next.snapQtyQuota,
        storage_cap_bytes_quota: next.storageCapBytesQuota,
      })
    } else {
      res = await API.updateTeamMemberResourceACL({
        res_id: memberResID,
        enable: next.enableACL,
        regions: next.regions,
        gpu_machine_cap: next.gpuMachineCap,
        cpu_machine_cap: next.cpuMachineCap,
        vgpu_machine_cap: next.vgpuMachineCap,
        processor_models: next.processorModels,
        official_image_cap: next.officialImageCap,
        images: next.images,
        mesh_net_quota: next.meshNetQuota,
        mesh_endpoint_quota: next.meshEndpointQuota,
        instance_qty_quota: next.instanceQtyQuota,
        snap_qty_quota: next.snapQtyQuota,
        storage_cap_bytes_quota: next.storageCapBytesQuota,
      })
      // close member ACL, delete member quotas
      if (res.code === 0 && !next.enableACL) {
        await API.deleteTeamMemberResourceQuotas?.({ res_id: memberResID })
        setHasPersonalQuotas(false)
        setMemberPresentTypes(new Set())
      }
    }
    if (res.code === 0) {
      setForm({ ...next, enableACL: true })
    }
    return res
  }

  const refetch = async () => {
    if (scope === 'team') {
      const team = await API.getTeamResourceACL({ res_id: teamResID })
      const teamF = fromTeamRes(team)
      setTeamUsingSystemDefault(!team.acl.options?.enableACL)

      setForm(teamF)
      setTeamDefaults(undefined)
      setMemberSnapshot(undefined)
      setHasPersonalQuotas(false)
      setMemberPresentTypes(new Set())
      return
    }

    const [team, member] = await Promise.all([
      API.getTeamResourceACL({ res_id: teamResID }),
      API.getTeamMemberResourceACL({ res_id: memberResID }),
    ])

    const teamF = fromTeamRes(team)
    setTeamDefaults(teamF)

    const { enableACL, form: memberF, presentTypes } = fromMemberRes(member)
    setMemberSnapshot(memberF)
    setMemberPresentTypes(presentTypes)
    setHasPersonalQuotas(presentTypes.size > 0)

    const next = enableACL ? memberF : { ...teamF, enableACL: false }

    setForm(next)
  }

  const save = async () => {
    if (scope === 'team') {
      const res = await patch(form)
      if (res.code !== 0) return
      await refetch()
      message.success(t('team.permissionQuota.saveTeamSuccess'))
      return
    }

    if (!form.enableACL) {
      const res = await patch({ ...form, enableACL: false })
      if (res.code !== 0) return
      await refetch()
      return
    }

    const res = await patch(form)
    if (res.code !== 0) return
    await refetch()
    message.success(t('team.permissionQuota.saveMemberSuccess'))
  }

  const enterMemberMode = () => {
    if (!teamDefaults) return
    const merged = mergeTeamAndMember(
      teamDefaults,
      memberSnapshot ?? emptyForm(),
      memberPresentTypes,
    )
    setForm(merged)
  }

  const resetFormToTeamDefaults = () => {
    if (!teamDefaults) return
    setForm({ ...teamDefaults, enableACL: true })
    message.success("Reset to team's permissions success")
  }

  const deleteMemberQuotas = async () => {
    if (scope !== 'member') return
    await API.deleteTeamMemberResourceQuotas?.({ res_id: memberResID })
    setHasPersonalQuotas(false)
    setMemberPresentTypes(new Set())
  }

  return {
    loading,
    form,
    setForm,
    teamDefaults,
    hasPersonalQuotas,
    memberSnapshot,
    patch,
    save,
    refetch,
    enterMemberMode,
    resetFormToTeamDefaults,
    deleteMemberQuotas,
    teamUsingSystemDefault,
    quotaLimits,
  }
}
